import { SearchEngine } from './common'

const defaultFieldSpecifiers = [
  { name: 'cpc' },
  { name: 'TI' },
  { name: 'AB' },
  { name: 'CL' },
  { name: 'before' },
  { name: 'after' },
  { name: 'priority' },
  { name: 'filing' },
  { name: 'publication' },
  { name: 'inventor' },
  { name: 'assignee' },
  { name: 'country' },
  { name: 'language' },
  { name: 'status' },
  { name: 'type' },
  { name: 'litigation' }
]

// const bracketSpaceBracketReplaceRE = new RegExp('\\) \\(', 'g')
const bracketOuterSetBracketReplaceRE = new RegExp('[()\']', 'g')

const andOperator = '&q='
const orOperator = ','

const defaultSyntaxGuideUrl = 'https://support.google.com/faqs/answer/7049475?hl=en-GB&ref_topic=6390989'

class GooglePatentEngine extends SearchEngine {
  constructor (fieldSpecifiers) {
    super('Google Patents')
    this.fieldSpecifiers = fieldSpecifiers || defaultFieldSpecifiers
    this.syntaxGuideUrl = defaultSyntaxGuideUrl
  }
  mapOperator (operator) {
    switch (operator.toUpperCase()) {
      case 'AND': return { op: andOperator, spaces: false }
      case 'OR': return { op: orOperator, spaces: false }
      case 'NOT': return { op: '-', spaces: false }
      case 'SAME':
      case 'WITH':
        return { op: '+' + operator + '+', spaces: false }
    }
    if (operator.startsWith('NEAR') || operator.startsWith('ADJ')) {
      return { op: '+' + operator + '+', spaces: false }
    }
    return { op: operator, spaces: true }
  }
  formulateQuery (query) {
    return 'https://patents.google.com/?' + this.cleanQuery(query)
  }
  cleanQuery (query) {
    // Apply regex based on operator
    const regexQuery = this.applyRegexLogic(query)
    // Reordering for non-field specifier terms to be first
    let { numberOfFieldSpecifiers, numberOfStandaloneTerms, sortedSplitCleanQuery } =
      this.getNumberFieldSpecifiersAndStandaloneTerms(regexQuery)
    // Setting start char and join operator based on query length and number of terms/field specifiers
    let { cleanedQuery, queryStartChar } = this.getStartChar(numberOfFieldSpecifiers, numberOfStandaloneTerms, sortedSplitCleanQuery)
    // Restructure if multiple of specific field specifier terms in OR group
    cleanedQuery = this.applyUniqueFieldSpecifierLogic(cleanedQuery)
    // Deal with corner case field specifier
    return queryStartChar + cleanedQuery
  }
  applyRegexLogic (query) {
    return query.indexOf('NEAR') === -1 || query.indexOf('ADJ') === -1
      ? query.replace(bracketOuterSetBracketReplaceRE, '')
      : query
  }
  // Return number of field specifiers and standalone terms for current query
  getNumberFieldSpecifiersAndStandaloneTerms (regexQuery) {
    let numberOfFieldSpecifiers = 0
    let numberOfStandaloneTerms = 0
    const sortedSplitCleanQuery = regexQuery.split(andOperator).reduce((acc, element) => {
      if (element.indexOf('=') === -1) {
        numberOfStandaloneTerms++
        return [element, ...acc]
      }
      numberOfFieldSpecifiers++
      return [...acc, element]
    }, [])
    return { numberOfFieldSpecifiers, numberOfStandaloneTerms, sortedSplitCleanQuery }
  }
  getStartChar (numberOfFieldSpecifiers, numberOfStandaloneTerms, sortedSplitCleanQuery) {
    let queryStartChar = 'q='
    let joinAndOperator = andOperator
    switch (numberOfFieldSpecifiers) {
      case 1:
        if (sortedSplitCleanQuery.length === 1 || numberOfStandaloneTerms === 0) {
          queryStartChar = ''
        }
        break
      default:
        if (numberOfStandaloneTerms === 0) {
          queryStartChar = ''
          joinAndOperator = '&'
        } else if (numberOfFieldSpecifiers > 0) {
          queryStartChar = 'q='
          joinAndOperator = '&'
        } else {
          queryStartChar = 'q='
        }
    }
    let cleanedQuery = sortedSplitCleanQuery.join(joinAndOperator)
    return { cleanedQuery, queryStartChar }
  }
  applyCornerCaseFieldSpecifiers (cleanedQuery) {
    const cornerCaseFieldSpecifierChanges = {
      '&TI=': '&q=TI%3d',
      '&cpc=': '&q=cpc%3d',
      '&AB=': '&q=AB%3d',
      '&CL=': '&q=CL$3d'
    }
    Object.keys(cornerCaseFieldSpecifierChanges).forEach((key) => {
      cleanedQuery = cleanedQuery.replaceAll(key, cornerCaseFieldSpecifierChanges[key])
    })
    return cleanedQuery
  }
  applyUniqueFieldSpecifierLogic (cleanedQuery) {
    const fieldSpecifiersRequiringReMapping = {
      'inventor': 0,
      'assignee': 0
    }
    let fieldSpecifierFlag = false
    if (cleanedQuery.indexOf(orOperator) !== -1) {
      const orOperatorSplitString = cleanedQuery.split(/[&,]+/) // AND and OR operator
      // loop through each field specifier for each OR'd element of the query
      for (let sortedSplitCleanQuerySplitIndex in orOperatorSplitString) {
        for (let fieldSpecifierKey in fieldSpecifiersRequiringReMapping) {
          const lengthOfFieldSpecifier = fieldSpecifierKey.length
          // Add a flag if the field specifier exists
          if (orOperatorSplitString[sortedSplitCleanQuerySplitIndex].slice(0, lengthOfFieldSpecifier) === fieldSpecifierKey) {
            fieldSpecifiersRequiringReMapping[fieldSpecifierKey] += 1
          }
        }
      }
      let termsWithFieldSpecifiers = []
      // Loop through each field specifier
      for (let fieldSpecifierKey in fieldSpecifiersRequiringReMapping) {
        switch (fieldSpecifiersRequiringReMapping[fieldSpecifierKey]) {
          case 1:
          case 0:
            break
          default:
            for (let element of orOperatorSplitString) {
              if (element.indexOf(fieldSpecifierKey + '=') !== -1) {
                termsWithFieldSpecifiers.append(fieldSpecifierKey[0] + element.split(fieldSpecifierKey + '='))
                fieldSpecifierFlag = true
              }
            }
        }
      }
      // Remapping for inventor and assignee inventor
      if (fieldSpecifierFlag) {
        let inventorFieldSpecifierTerms = []
        let assigneeFieldSpecifierTerms = []
        termsWithFieldSpecifiers.forEach((x) => (x.indexOf('i,') === 0 ? inventorFieldSpecifierTerms : assigneeFieldSpecifierTerms).push(x.slice(2)))
        const cleanedInventorFieldSpecifierTerms = inventorFieldSpecifierTerms.length > 0 ? 'inventor=' + inventorFieldSpecifierTerms.join(orOperator) : ''
        const cleanedAssigneeFieldSpecifierTerms = assigneeFieldSpecifierTerms.length > 0 ? 'assignee=' + assigneeFieldSpecifierTerms.join(orOperator) : ''
        if (cleanedInventorFieldSpecifierTerms.length > 0 && cleanedAssigneeFieldSpecifierTerms.length) {
          cleanedQuery = cleanedInventorFieldSpecifierTerms + '&' + cleanedAssigneeFieldSpecifierTerms
        } else if (cleanedInventorFieldSpecifierTerms.length === 0) {
          cleanedQuery = cleanedAssigneeFieldSpecifierTerms
        } else if (cleanedAssigneeFieldSpecifierTerms.length === 0) {
          cleanedQuery = cleanedInventorFieldSpecifierTerms
        }
      }
    }
    return this.applyCornerCaseFieldSpecifiers(cleanedQuery)
  }
  formulateQueryFromNodeData (nodeData) {
    const baseTerm = nodeData.text.replace(bracketOuterSetBracketReplaceRE, '')
    return this.formTerm(baseTerm, nodeData.isNegative, false, nodeData.fieldSpecifiers)
  }
  fieldSpecifierReMapping (r, text, fieldSpecifier, delimiter) {
    const textArray = text.split(' ')
    if (textArray.length > 1) {
      r += `${fieldSpecifier}=${textArray[0]}`
      for (let i = 1; i < textArray.length; i++) {
        r += `+${textArray[i]}`
      }
    } else {
      r += fieldSpecifier + delimiter + text
    }
    return r
  }
  formTerm (text, isNegative, addQuotes, fieldSpecifier) {
    let r = ''
    if (isNegative) {
      r += '-'
    }
    if (fieldSpecifier && fieldSpecifier.length > 0) {
      // Remap char for google patents
      text = text.replace('/', '%2f')
      switch (fieldSpecifier) {
        case 'cpc':
        case 'TI':
        case 'AB':
        case 'CL':
          r += this.fieldSpecifierReMapping(r, text, fieldSpecifier, '%3d')
          break
        default:
          r += this.fieldSpecifierReMapping(r, text, fieldSpecifier, '=')
      }
    } else {
      r += text
    }
    return r
  }
  lintQuery (queryGraphData) {
    const m = this.lintFieldSpecifiers(queryGraphData, this.fieldSpecifiers)
    return m ? [m] : []
  }
}

export default GooglePatentEngine



// WEBPACK FOOTER //
// ./src/formulate_queries/GooglePatentEngine.js